export const EMBED_PLACEHOLDER_HEIGHT = 300;
const DEFAULT_IFRAME_HEIGHT_ATTR = "315";
const SIZE_MESSAGE = "SB_EMBED_SIZE";

function iframeManagesOwnHeight(frame: Element): boolean {
  const style = (frame.getAttribute("style") || "").toLowerCase();
  if (/height\s*:\s*auto/.test(style)) return true;
  const src = (frame.getAttribute("src") || "").toLowerCase();
  if (src.includes("/component/")) return true;
  const sibling = frame.nextElementSibling;
  return (
    sibling?.tagName === "SCRIPT" &&
    (sibling.textContent || "").includes(SIZE_MESSAGE)
  );
}

export function isSelfSizingEmbedHtml(html: string): boolean {
  const lower = html.toLowerCase();
  if (lower.includes("sb_embed_size") || lower.includes("/component/")) {
    return true;
  }
  // Only match iframe style="height:auto" — not unrelated CSS like `height: auto`
  // inside Flodesk / form embed stylesheets (those false-positives disabled
  // intrinsic measurement and left forms stuck at the 300px placeholder).
  return /<iframe\b[^>]*\bstyle\s*=\s*("[^"]*height\s*:\s*auto[^"]*"|'[^']*height\s*:\s*auto[^']*')/i.test(
    html,
  );
}


function appendWidthStyle(el: Element, extra: string) {
  const existing = el.getAttribute("style") || "";
  el.setAttribute(
    "style",
    existing
      ? `${existing}${existing.trim().endsWith(";") ? "" : ";"}${extra}`
      : extra
  );
}

/** Widen media embeds without locking Copy-HTML iframes to the 315px fallback. */
export function makeEmbedResponsive(rawHtml: string): string {
  try {
    const temp = document.createElement("div");
    temp.innerHTML = rawHtml;
    const widthStyle = "width:100%;max-width:100%;display:block;";
    temp.querySelectorAll("iframe").forEach((frame) => {
      frame.setAttribute("width", "100%");
      if (iframeManagesOwnHeight(frame)) {
        frame.removeAttribute("height");
      } else if (!frame.getAttribute("height")) {
        frame.setAttribute("height", DEFAULT_IFRAME_HEIGHT_ATTR);
      }
      appendWidthStyle(frame, widthStyle);
    });
    temp.querySelectorAll("embed, object, video").forEach((el) => {
      el.setAttribute("width", "100%");
      appendWidthStyle(el, widthStyle);
    });
    return temp.innerHTML;
  } catch (_err) {
    return rawHtml;
  }
}

export function parseEmbedHeight(html: string): number {
  const match = html.match(/<iframe[^>]*\sheight=["']?(\d+)/i);
  return match ? parseInt(match[1], 10) : EMBED_PLACEHOLDER_HEIGHT;
}

export function buildEmbedSrcDoc(
  html: string,
  options?: { formEmbed?: boolean },
): string {
  const formCss = options?.formEmbed
    ? `
  [data-ff-el="root"] {
    box-sizing: border-box !important;
  }
  /* Full-bleed success/captcha card so it doesn't sit as a narrow peach strip */
  [data-ff-el="root"][data-ff-stage="success"] [class*="__container"],
  [data-ff-el="root"].fd-has-success [class*="__container"],
  [data-ff-el="root"].fd-has-captcha [class*="__container"] {
    width: 100% !important;
    max-width: 100% !important;
  }
  [data-ff-el="root"][data-ff-stage="success"] [class*="__form"],
  [data-ff-el="root"].fd-has-success [class*="__form"] {
    padding-top: 40px !important;
    padding-bottom: 40px !important;
  }
  [data-ff-el="root"][data-ff-stage="success"] [class*="__success-message"],
  [data-ff-el="root"].fd-has-success [class*="__success-message"] {
    padding: 8px 16px 16px !important;
  }
`
    : "";

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<style>
  html, body {
    margin: 0; padding: 0; background: transparent;
    width: 100%; overflow-x: hidden; box-sizing: border-box;
    height: auto !important; min-height: 0 !important;
  }
  iframe, video {
    display: block;
    width: 100% !important;
    max-width: 100% !important;
    box-sizing: border-box !important;
  }
  iframe[src*="/component/"] {
    margin-top: 0 !important;
    margin-bottom: 0 !important;
  }
  form, .formkit-form, [data-sv-form], .ck-form,
  .mc-embed-signup, [class*="formkit"], [class*="ck_form"], [class*="mc_embed"],
  [data-ff-el="root"], [class*="ff-"][data-ff-el], .fd-form-content {
    width: 100% !important; max-width: 100% !important; min-width: unset !important;
  }
  input[type="email"], input[type="text"], input[type="tel"],
  .formkit-input, .ck_email_address, .ck_first_name {
    width: 100% !important; max-width: 100% !important;
    min-width: unset !important; box-sizing: border-box !important;
  }
  ${formCss}
</style>
</head>
<body>${html}</body>
</html>`;
}

function findSbEmbedIframe(
  root: ParentNode,
  event: MessageEvent
): HTMLIFrameElement | null {
  const iframes = Array.from(root.querySelectorAll("iframe")) as HTMLIFrameElement[];
  const bySource = iframes.find((iframe) => iframe.contentWindow === event.source);
  if (bySource) return bySource;
  // Untrusted messages from other frames on the page must not resize this embed.
  if (event.source) return null;
  if (iframes.length === 1) return iframes[0];
  return (
    iframes.find((iframe) => (iframe.getAttribute("src") || "").includes("/component/")) ||
    null
  );
}

export function applySbEmbedSizeToMatchingIframe(
  root: ParentNode,
  event: MessageEvent
): boolean {
  const data = event.data;
  if (!data || data.type !== SIZE_MESSAGE || !data.height) return false;
  const height = Math.ceil(Number(data.height));
  if (!(height > 0)) return false;
  const iframe = findSbEmbedIframe(root, event);
  if (!iframe) return false;
  iframe.style.height = `${height}px`;
  iframe.style.overflow = "hidden";
  return true;
}

function verticalSpaceAround(el: HTMLElement): number {
  const style = window.getComputedStyle(el);
  return (parseFloat(style.marginTop) || 0) + (parseFloat(style.marginBottom) || 0);
}

const SRCDOC_SKIP_TAGS = new Set([
  "SCRIPT",
  "LINK",
  "STYLE",
  "META",
  "NOSCRIPT",
  "BASE",
]);

function measureSrcDocContentHeight(doc: Document): number {
  const body = doc.body;
  if (!body) return 0;
  const bodyTop = body.getBoundingClientRect().top;
  let bottom = 0;
  Array.from(body.children).forEach((node) => {
    const el = node as HTMLElement;
    if (!el.getBoundingClientRect) return;
    if (SRCDOC_SKIP_TAGS.has(el.tagName)) return;
    bottom = Math.max(bottom, el.getBoundingClientRect().bottom - bodyTop);
  });
  if (bottom > 0) return Math.ceil(bottom);
  // Fallback only when there are no measurable children yet.
  return Math.ceil(
    Math.max(body.scrollHeight || 0, doc.documentElement?.scrollHeight || 0),
  );
}

export function listenForSbEmbedSize(root: HTMLElement | null): () => void {
  if (!root) return () => {};
  const onMessage = (event: MessageEvent) => {
    applySbEmbedSizeToMatchingIframe(root, event);
  };
  window.addEventListener("message", onMessage);
  return () => window.removeEventListener("message", onMessage);
}

export function attachSrcDocEmbedHeight(
  frame: HTMLIFrameElement,
  onHeight: (height: number) => void,
  options?: { requireSizeMessage?: boolean; formEmbed?: boolean }
): () => void {
  let childCleanup: (() => void) | undefined;
  let destroyed = false;
  let lastHeight = 0;
  let measureRaf = 0;
  let allowIntrinsicMeasure = !options?.requireSizeMessage;
  const formEmbed = Boolean(options?.formEmbed);

  const publishHeight = (next: number, fromMessage = false) => {
    if (destroyed || !(next > 0)) return;
    // Hysteresis avoids ResizeObserver ↔ setHeight feedback loops (and the
    // CRA "ResizeObserver loop completed" overlay) when measure ±2 oscillates.
    if (!fromMessage && Math.abs(next - lastHeight) < 3) return;
    if (next === lastHeight) return;
    lastHeight = next;
    onHeight(next);
  };

  const measure = () => {
    if (destroyed || !allowIntrinsicMeasure) return;
    try {
      const doc = frame.contentDocument;
      if (!doc?.body) return;
      // Success/captcha must be allowed to shrink — keeping the old form height
      // left a tall transparent iframe and showed the yellow section behind it.
      const next = measureSrcDocContentHeight(doc) + 2;
      if (formEmbed) {
        const root = doc.querySelector<HTMLElement>("[data-ff-el='root']");
        if (root) root.style.minHeight = "";
      }
      publishHeight(next);
    } catch {
      /* srcdoc not ready */
    }
  };

  const scheduleMeasure = () => {
    if (destroyed || measureRaf) return;
    measureRaf = window.requestAnimationFrame(() => {
      measureRaf = 0;
      measure();
    });
  };

  const applySizeMessage = (event: MessageEvent, root: ParentNode) => {
    if (!applySbEmbedSizeToMatchingIframe(root, event)) return;
    const iframe = findSbEmbedIframe(root, event);
    const extra = iframe ? verticalSpaceAround(iframe) : 0;
    publishHeight(Math.ceil(Number(event.data.height)) + extra + 2, true);
  };

  const bindDocument = () => {
    childCleanup?.();
    const win = frame.contentWindow;
    const doc = frame.contentDocument;
    if (!win || !doc) return;

    doc.querySelectorAll("iframe").forEach((iframe) => {
      const src = iframe.getAttribute("src") || "";
      const sibling = iframe.nextElementSibling;
      const scriptText =
        sibling?.tagName === "SCRIPT" ? sibling.textContent || "" : "";
      const styleAttr = (iframe.getAttribute("style") || "").toLowerCase();
      const selfSizing =
        scriptText.includes(SIZE_MESSAGE) ||
        /height\s*:\s*auto/.test(styleAttr) ||
        src.includes("/component/");
      if (selfSizing) {
        iframe.removeAttribute("height");
        iframe.setAttribute("loading", "eager");
      }
      iframe.addEventListener("load", () => {
        allowIntrinsicMeasure = true;
        scheduleMeasure();
      });
    });

    const onSrcDocMessage = (event: MessageEvent) => applySizeMessage(event, doc);
    win.addEventListener("message", onSrcDocMessage);

    // Watch Flodesk stage/captcha DOM changes for the life of the embed (submit
    // often happens after the initial hydrate poll has stopped).
    let mutationObserver: MutationObserver | undefined;
    let mutationTimer = 0;
    try {
      mutationObserver = new MutationObserver(() => {
        if (mutationTimer) return;
        mutationTimer = window.setTimeout(() => {
          mutationTimer = 0;
          allowIntrinsicMeasure = true;
          scheduleMeasure();
        }, 50);
      });
      mutationObserver.observe(doc.documentElement || doc.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["class", "data-ff-stage", "style"],
      });
    } catch {
      /* ignore */
    }

    childCleanup = () => {
      win.removeEventListener("message", onSrcDocMessage);
      mutationObserver?.disconnect();
      if (mutationTimer) window.clearTimeout(mutationTimer);
    };
    scheduleMeasure();
  };

  const onHostMessage = (event: MessageEvent) => {
    const doc = frame.contentDocument;
    if (!doc) return;
    applySizeMessage(event, doc);
  };

  frame.addEventListener("load", bindDocument);
  window.addEventListener("message", onHostMessage);
  bindDocument();

  // Flodesk / form kits hydrate and reflow (fonts, stacked mobile fields) after
  // the first paint — keep measuring until the size settles.
  const timers = [200, 600, 1200, 2000, 3500, 5000].map((ms) =>
    window.setTimeout(() => {
      allowIntrinsicMeasure = true;
      measure();
    }, ms),
  );
  const fallback = window.setTimeout(() => {
    allowIntrinsicMeasure = true;
    measure();
  }, 1200);
  const poll = window.setInterval(() => {
    allowIntrinsicMeasure = true;
    measure();
  }, 500);
  const stopPoll = window.setTimeout(() => clearInterval(poll), 8000);

  const onFrameResize = () => {
    allowIntrinsicMeasure = true;
    scheduleMeasure();
  };
  window.addEventListener("resize", onFrameResize);

  try {
    const fonts = frame.contentDocument?.fonts;
    fonts?.ready?.then(() => {
      allowIntrinsicMeasure = true;
      scheduleMeasure();
    });
  } catch {
    /* ignore */
  }

  return () => {
    destroyed = true;
    frame.removeEventListener("load", bindDocument);
    window.removeEventListener("message", onHostMessage);
    window.removeEventListener("resize", onFrameResize);
    childCleanup?.();
    timers.forEach(clearTimeout);
    clearTimeout(fallback);
    clearInterval(poll);
    clearTimeout(stopPoll);
    if (measureRaf) window.cancelAnimationFrame(measureRaf);
  };
}
